import type { InfiniteData } from "@tanstack/react-query"
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import {
  createDepositV2,
  createJpycDepositV2,
  depositGasAccountTransactionV1,
  getAccountingAddressBalancesV2,
  getAccountingHistoryV2,
  getDepositTokensV2,
  getDepositV2,
  getDepositVaultsV1,
  getDepositVaultsV2,
  getGasAccountBalanceV1,
  getGasAccountTransactionByIdV1,
  getGasAccountTransactionsV1,
  getStableTokensV1,
  quoteJpycDepositV2,
  updateDepositV2,
  updateGasAccountBalancesV1,
  postUseGasAccountBalanceV1,
  type AccountingAddressBalanceV2Response,
  type AccountingHistoryV2Response,
  type CreateDepositV2Request,
  type CreateJpycDepositV2Request,
  type DepositRequest,
  type DepositTokenType,
  type DepositTokensV2Response,
  type DepositV2Response,
  type DepositVaultsResponse,
  type GasAccountBalanceResponse,
  type GasAccountTransactionDetailResponse,
  type GasAccountTransactionItem,
  type GasAccountTransactionListData,
  type JpycDepositQuoteV2Request,
  type StableTokensResponse,
  type UpdateBalancesRequest,
  type UpdateDepositV2Request,
  type UseBalanceRequest,
} from "@/services/gas-account"

export const GAS_ACCOUNT_QUERY_KEYS = {
  TRANSACTIONS: ["gas-account", "transactions"] as const,
  TRANSACTIONS_V1: ["gas-account", "transactions", "v1"] as const,
  ACCOUNTING_HISTORY_V2: ["gas-account", "accounting-history", "v2"] as const,
  TRANSACTION_DETAIL: (id: string) =>
    ["gas-account", "transactions", id] as const,
  TRANSACTION_DETAIL_V1: (id: string) =>
    ["gas-account", "transactions", "v1", id] as const,
  STABLE_TOKENS: ["gas-account", "stable-tokens"] as const,
  STABLE_TOKENS_V1: ["gas-account", "stable-tokens", "v1"] as const,
  DEPOSIT_TOKENS_V2: (tokenType?: DepositTokenType, chainId?: number) =>
    ["gas-account", "deposit-tokens", "v2", tokenType ?? "all", chainId ?? "all"] as const,
  DEPOSIT_VAULTS: ["gas-account", "deposit-vaults"] as const,
  DEPOSIT_VAULTS_V1: ["gas-account", "deposit-vaults", "v1"] as const,
  DEPOSIT_VAULTS_V2: ["gas-account", "deposit-vaults", "v2"] as const,
  BALANCE: ["gas-account", "balance"] as const,
  BALANCE_V1: ["gas-account", "balance", "v1"] as const,
  ACCOUNTING_BALANCE_V2: ["gas-account", "accounting-balance", "v2"] as const,
  DEPOSIT_DETAIL_V2: (id: string) =>
    ["gas-account", "deposit", "v2", id] as const,
}

export type { GasAccountTransactionItem }

const EMPTY_META = {
  current_page: 1,
  next_page: null,
  prev_page: null,
  total_pages: 0,
  total_count: 0,
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useGasAccountTransactions(
  params?: { per?: number },
  enabled = true
) {
  const { data, ...query } = useInfiniteQuery({
    queryKey: [...GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS_V1, params],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await getGasAccountTransactionsV1({
          ...params,
          page: pageParam,
        })
        return {
          data: response.data,
          meta: response.meta,
        }
      } catch {
        return {
          data: {
            address: "",
            transactions: [],
            filters: { type: null, status: null },
          },
          meta: EMPTY_META,
        }
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined
      const { current_page, total_pages } = lastPage.meta
      return current_page < total_pages ? current_page + 1 : undefined
    },
    initialPageParam: 1,
    enabled,
    staleTime: 30000,
  })

  const allTransactions: GasAccountTransactionItem[] =
    data?.pages?.flatMap((page) => page?.data?.transactions ?? []) ?? []

  return {
    data: allTransactions,
    listData: data?.pages?.map((p) => p.data) ?? [],
    meta: data?.pages?.[data.pages.length - 1]?.meta,
    ...query,
  }
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useGasAccountTransactionsPaginated(
  params?: { per?: number; page?: number },
  enabled = true
) {
  const { per = 10, page = 1 } = params ?? {}
  const queryResult = useQuery({
    queryKey: [
      ...GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS_V1,
      "paginated",
      { per, page },
    ],
    queryFn: async () => {
      try {
        const response = await getGasAccountTransactionsV1({ per, page })
        return {
          transactions: response.data.transactions,
          meta: response.meta,
        }
      } catch {
        return {
          transactions: [] as GasAccountTransactionItem[],
          meta: EMPTY_META,
        }
      }
    },
    enabled,
    staleTime: 30000,
  })

  return {
    transactions: queryResult.data?.transactions ?? [],
    meta: queryResult.data?.meta,
    ...queryResult,
  }
}

export function useAccountingHistoryV2(
  params?: { per?: number; page?: number; status?: string },
  enabled = true
) {
  const { per = 10, page = 1, status } = params ?? {}
  const queryResult = useQuery<AccountingHistoryV2Response>({
    queryKey: [
      ...GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_HISTORY_V2,
      { per, page, status },
    ],
    queryFn: () => getAccountingHistoryV2({ per, page, status }),
    enabled,
    staleTime: 30000,
  })

  return {
    transactions: queryResult.data?.data ?? [],
    meta: queryResult.data?.meta,
    ...queryResult,
  }
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useGasAccountTransaction(id: string, enabled = true) {
  return useQuery<GasAccountTransactionDetailResponse>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.TRANSACTION_DETAIL_V1(id),
    queryFn: () => getGasAccountTransactionByIdV1(id),
    enabled: enabled && !!id,
    staleTime: 30000,
  })
}

type TransactionListPage = {
  data: GasAccountTransactionListData
  meta: {
    current_page: number
    next_page: number | null
    prev_page: number | null
    total_pages: number
    total_count: number
  }
}

type PaginatedTransactionData = {
  transactions: GasAccountTransactionItem[]
  meta: {
    current_page: number
    next_page: number | null
    prev_page: number | null
    total_pages: number
    total_count: number
  }
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useRefreshTransactionDetail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => getGasAccountTransactionByIdV1(id),
    onSuccess: (response, id) => {
      const updated = response?.data?.transaction
      if (!updated) return

      queryClient.setQueriesData<InfiniteData<TransactionListPage>>(
        { queryKey: GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS_V1, exact: false },
        (old) => {
          if (!old?.pages) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                transactions: page.data.transactions.map((t) =>
                  t.id === id ? { ...t, ...updated } : t
                ),
              },
            })),
          }
        }
      )

      queryClient.setQueriesData<PaginatedTransactionData>(
        {
          queryKey: [...GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS_V1, "paginated"],
          exact: false,
        },
        (old) => {
          if (!old?.transactions) return old
          return {
            ...old,
            transactions: old.transactions.map((t) =>
              t.id === id ? { ...t, ...updated } : t
            ),
          }
        }
      )

      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.TRANSACTION_DETAIL_V1(id),
      })
    },
  })
}

/** Legacy v1 token hook. Kept temporarily during v2 rollout. */
export function useStableTokensV1(enabled = true) {
  return useQuery<StableTokensResponse>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.STABLE_TOKENS_V1,
    queryFn: () => getStableTokensV1(),
    enabled,
    staleTime: 60 * 60 * 1000,
  })
}

export function useDepositTokensV2(
  params?: { tokenType?: DepositTokenType; chainId?: number },
  enabled = true
) {
  return useQuery<DepositTokensV2Response>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_TOKENS_V2(
      params?.tokenType,
      params?.chainId
    ),
    queryFn: () => getDepositTokensV2(params),
    enabled,
    staleTime: 60 * 60 * 1000,
  })
}

/** Backward-compatible shared token hook now reads v2 deposit tokens. */
export function useStableTokens(enabled = true) {
  return useDepositTokensV2(undefined, enabled)
}

/** Legacy v1 vault hook. Kept temporarily during v2 rollout. */
export function useDepositVaultsV1(enabled = true) {
  return useQuery<DepositVaultsResponse>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_VAULTS_V1,
    queryFn: () => getDepositVaultsV1(),
    enabled,
    staleTime: Infinity,
  })
}

export function useDepositVaultsV2(enabled = true) {
  return useQuery<DepositVaultsResponse>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_VAULTS_V2,
    queryFn: () => getDepositVaultsV2(),
    enabled,
    staleTime: Infinity,
  })
}

/** Backward-compatible shared vault hook now reads v2 deposit vaults. */
export function useDepositVaults(enabled = true) {
  return useDepositVaultsV2(enabled)
}

/** Legacy v1 balance hook. Kept temporarily during v2 rollout. */
export function useGasAccountBalanceQueryV1(
  address: string | undefined,
  enabled = true
) {
  const shouldFetch = !!address && enabled
  return useQuery<GasAccountBalanceResponse>({
    queryKey: [...GAS_ACCOUNT_QUERY_KEYS.BALANCE_V1, address ?? ""],
    queryFn: () => getGasAccountBalanceV1(),
    enabled: shouldFetch,
    staleTime: 30000,
  })
}

export function useAccountingAddressBalanceV2(
  address: string | undefined,
  enabled = true
) {
  const shouldFetch = !!address && enabled
  return useQuery<AccountingAddressBalanceV2Response>({
    queryKey: [...GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_BALANCE_V2, address ?? ""],
    queryFn: () => getAccountingAddressBalancesV2(),
    enabled: shouldFetch,
    staleTime: 30000,
  })
}

/** Backward-compatible balance hook remains v1 until account UI is migrated. */
export function useGasAccountBalanceQuery(
  address: string | undefined,
  enabled = true
) {
  return useGasAccountBalanceQueryV1(address, enabled)
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useUpdateGasAccountBalances() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateBalancesRequest) =>
      updateGasAccountBalancesV1(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.BALANCE })
      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_BALANCE_V2,
      })
    },
  })
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useDepositGasAccountTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: DepositRequest) =>
      depositGasAccountTransactionV1(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS })
      queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.BALANCE })
      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_HISTORY_V2,
      })
      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_BALANCE_V2,
      })
    },
  })
}

function invalidateGasAccountV2(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_HISTORY_V2,
  })
  queryClient.invalidateQueries({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_BALANCE_V2,
  })
  queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS })
  queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.BALANCE })
}

export function useCreateDepositV2() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateDepositV2Request) => createDepositV2(payload),
    onSuccess: (response) => {
      invalidateGasAccountV2(queryClient)
      if (response.data?.id) {
        queryClient.invalidateQueries({
          queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_DETAIL_V2(response.data.id),
        })
      }
    },
  })
}

export function useUpdateDepositV2() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      depositId,
      payload,
    }: {
      depositId: string
      payload: UpdateDepositV2Request
    }) => updateDepositV2(depositId, payload),
    onSuccess: (response, variables) => {
      invalidateGasAccountV2(queryClient)
      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_DETAIL_V2(
          response.data?.id || variables.depositId
        ),
      })
    },
  })
}

export function useDepositDetailV2(depositId: string | null, enabled = true) {
  return useQuery<DepositV2Response>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_DETAIL_V2(depositId ?? ""),
    queryFn: () => getDepositV2(depositId ?? ""),
    enabled: enabled && !!depositId,
    staleTime: 5000,
  })
}

export function usePollDepositV2(depositId: string | null, enabled = true) {
  return useQuery<DepositV2Response>({
    queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_DETAIL_V2(depositId ?? ""),
    queryFn: () => getDepositV2(depositId ?? ""),
    enabled: enabled && !!depositId,
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status
      if (!status || status === "PENDING") return 3000
      return false
    },
  })
}

export function useQuoteJpycDepositV2() {
  return useMutation({
    mutationFn: (payload: JpycDepositQuoteV2Request) =>
      quoteJpycDepositV2(payload),
  })
}

export function useCreateJpycDepositV2() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateJpycDepositV2Request) =>
      createJpycDepositV2(payload),
    onSuccess: (response) => {
      invalidateGasAccountV2(queryClient)
      if (response.data?.id) {
        queryClient.invalidateQueries({
          queryKey: GAS_ACCOUNT_QUERY_KEYS.DEPOSIT_DETAIL_V2(response.data.id),
        })
      }
    },
  })
}

/** Legacy v1. Kept temporarily during v2 rollout. */
export function useUseGasAccountBalance() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UseBalanceRequest) =>
      postUseGasAccountBalanceV1(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.TRANSACTIONS })
      queryClient.invalidateQueries({ queryKey: GAS_ACCOUNT_QUERY_KEYS.BALANCE })
      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_HISTORY_V2,
      })
      queryClient.invalidateQueries({
        queryKey: GAS_ACCOUNT_QUERY_KEYS.ACCOUNTING_BALANCE_V2,
      })
    },
  })
}
